import { useMemo } from "react";

import Beacon from "@gx/dls/beacons/Beacon";
import Chip, { ChipSize, ChipVariant } from "@gx/dls/chips/Chip";
import FlexWrapper, { AlignItems } from "@gx/dls/containers/FlexWrapper";
import Text, { TextSize, TextVariant } from "@gx/dls/text/Text";
import TextShimmer from "@gx/dls/text/TextShimmer";
import { SourceStatus } from "@gx/ts-contracts/services/database/models/public_pb";
import { SourceResource } from "@gx/ts-contracts/services/source/resources/source_pb";

import { SOURCE_STATUS_TO_BEACON_VARIANT_MAP } from "@/pages/sources/constants";

import { convertProtobufTimestampToDate, getTimeAgo } from "@/utils/datetimes";

interface SelectProjectSourceCardStatusProps extends Pick<
  SourceResource,
  "updatedAt" | "sourceStatus"
> {
  isLoading: boolean;
  isExisting?: boolean;
}

const SelectProjectSourceCardStatus = ({
  updatedAt,
  sourceStatus,
  isLoading,
  isExisting,
}: SelectProjectSourceCardStatusProps) => {
  const lastSyncedAt = useMemo(() => {
    return updatedAt ? getTimeAgo(convertProtobufTimestampToDate(updatedAt)) : null;
  }, [updatedAt]);

  const lastSyncedLabel = useMemo(() => {
    if (sourceStatus === SourceStatus.UNSPECIFIED) {
      return "No syncs yet";
    }
    if (sourceStatus === SourceStatus.PENDING) {
      return `Syncing ${lastSyncedAt?.toLowerCase()}`;
    }
    if (sourceStatus === SourceStatus.READY) {
      return `Last synced ${lastSyncedAt?.toLowerCase()}`;
    }
    if (sourceStatus === SourceStatus.FAILED) {
      return `Failed to sync ${lastSyncedAt?.toLowerCase()}`;
    }
    return null;
  }, [lastSyncedAt, sourceStatus]);

  return (
    <FlexWrapper alignItems={AlignItems.CENTER} gap={16}>
      {isExisting ? (
        <Chip label="Source added" size={ChipSize.SMALL} variant={ChipVariant.SUCCESS} />
      ) : isLoading ? (
        <TextShimmer height={16} width={120} />
      ) : lastSyncedAt ? (
        <Text size={TextSize.BODY_SM} variant={TextVariant.TERTIARY}>
          {lastSyncedLabel}
        </Text>
      ) : (
        <Text size={TextSize.BODY_SM} variant={TextVariant.TERTIARY}>
          No syncs yet
        </Text>
      )}
      {isLoading ? (
        <TextShimmer height={8} width={8} />
      ) : (
        <Beacon variant={SOURCE_STATUS_TO_BEACON_VARIANT_MAP[sourceStatus]} isPulse={!isExisting} />
      )}
    </FlexWrapper>
  );
};

export default SelectProjectSourceCardStatus;
